import { supabase } from '../supabaseClient';
import { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';

// Permission checking utilities
export class PermissionManager {
  static userCompanyContext = null;
  static userPermissions = new Map();

  // Initialize user context and permissions
  static async initializeUserContext() {
    try {
      const { data: context, error: contextError } = await supabase
        .rpc('get_user_company_context');

      if (contextError) {
        console.error('Error getting user company context:', contextError);
        throw contextError;
      }
      
      // Handle both array and single object responses
      if (Array.isArray(context)) {
        this.userCompanyContext = context[0] || null;
      } else {
        this.userCompanyContext = context || null;
      }

      if (this.userCompanyContext) {
        await this.loadUserPermissions();
      }

      return this.userCompanyContext;
    } catch (error) {
      console.error('Failed to initialize user context:', error);
      this.userCompanyContext = null;
      return null;
    }
  }

  // Load all user permissions into memory
  static async loadUserPermissions() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: companyUser } = await supabase
        .from('company_users')
        .select(`
          role_id,
          roles!inner (
            role_permissions!inner (
              can_read,
              can_write,
              can_delete,
              permissions!inner (
                name,
                category,
                resource
              )
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      this.userPermissions.clear();

      if (companyUser?.roles?.role_permissions) {
        companyUser.roles.role_permissions.forEach(rp => {
          const permission = rp.permissions;
          this.userPermissions.set(permission.name, {
            name: permission.name,
            category: permission.category,
            resource: permission.resource,
            can_read: rp.can_read,
            can_write: rp.can_write,
            can_delete: rp.can_delete
          });
        });
      }
    } catch (error) {
      this.userPermissions.clear();
    }
  }

  // Check if user has specific permission
  static hasPermission(permissionName, accessType = 'read') {
    if (!this.userPermissions.has(permissionName)) {
      return false;
    }

    const permission = this.userPermissions.get(permissionName);
    
    switch (accessType.toLowerCase()) {
      case 'read':
        return permission.can_read;
      case 'write':
      case 'edit':
        return permission.can_write;
      case 'delete':
        return permission.can_delete;
      default:
        return false;
    }
  }

  // Check if user can access settings
  static canAccessSettings() {
    return this.userCompanyContext?.can_access_settings || false;
  }

  // Check if user is admin level or above
  static isAdminOrAbove() {
    return this.userCompanyContext?.hierarchy_level <= 2;
  }

  // Check if user is manager level or above
  static isManagerOrAbove() {
    return this.userCompanyContext?.hierarchy_level <= 3;
  }

  // Check if user is company owner
  static isCompanyOwner() {
    return this.userCompanyContext?.role_name === 'super_admin' && this.userCompanyContext?.hierarchy_level === 1;
  }

  // Check if user can manage other users (not themselves)
  static canManageUsers() {
    return this.isAdminOrAbove() || this.isCompanyOwner();
  }

  // Get user's role name
  static getUserRole() {
    return this.userCompanyContext?.role_name || 'unknown';
  }

  // Get user's company ID
  static getCompanyId() {
    return this.userCompanyContext?.company_id || null;
  }

  // Get user's hierarchy level
  static getHierarchyLevel() {
    return this.userCompanyContext?.hierarchy_level || 999;
  }

  // Check if user can access any product inventory tab
  static canAccessAnyProductInventoryTab() {
    const productInventoryPermissions = [
      'inventory.products.overview.view',
      'inventory.products.inventory.view', 
      'inventory.products.transactions.view',
      'inventory.products.configurations.view'
    ];
    
    return productInventoryPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any formulations tab
  static canAccessAnyFormulationsTab() {
    const formulationsPermissions = [
      'products.formulations.overview.view',
      'products.formulations.ingredients.view',
      'products.formulations.procedures.view',
      'products.formulations.tests.view',
      'products.formulations.costs.view',
      'products.formulations.summary.view'
    ];
    
    return formulationsPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any raw materials inventory tab
  static canAccessAnyRawMaterialsTab() {
    const rawMaterialsPermissions = [
      'inventory.raw_materials.overview.view',
      'inventory.raw_materials.inventory.view',
      'inventory.raw_materials.transactions.view',
      'inventory.raw_materials.configurations.view'
    ];
    
    return rawMaterialsPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any bottles inventory tab
  static canAccessAnyBottlesTab() {
    const bottlesPermissions = [
      'inventory.bottles.overview.view',
      'inventory.bottles.inventory.view',
      'inventory.bottles.transactions.view',
      'inventory.bottles.configurations.view'
    ];
    
    return bottlesPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any caps inventory tab
  static canAccessAnyCapsTab() {
    const capsPermissions = [
      'inventory.caps.overview.view',
      'inventory.caps.inventory.view',
      'inventory.caps.transactions.view',
      'inventory.caps.configurations.view'
    ];
    
    return capsPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any labels inventory tab
  static canAccessAnyLabelsTab() {
    const labelsPermissions = [
      'inventory.labels.overview.view',
      'inventory.labels.inventory.view',
      'inventory.labels.transactions.view',
      'inventory.labels.configurations.view'
    ];
    
    return labelsPermissions.some(permission => this.hasPermission(permission));
  }

  // Check if user can access any supplies inventory tab
  static canAccessAnySuppliesTab() {
    const suppliesPermissions = [
      'inventory.supplies.overview.view',
      'inventory.supplies.inventory.view',
      'inventory.supplies.transactions.view',
      'inventory.supplies.configurations.view'
    ];
    
    return suppliesPermissions.some(permission => this.hasPermission(permission));
  }

  // Check permissions for specific pages/components
  static canAccessPage(pageName) {
    const pagePermissions = {
      dashboard: 'dashboard.view',
      calendar: 'calendar.view',
      schedule: 'schedule.view',
      sales_overview: 'sales.overview.view',
      customers: 'sales.customers.view',
      invoices: 'sales.invoices.view',
      price_lists: 'sales.price_lists.view',
      purchase_orders: 'sales.purchase_orders.page.view',
      purchase_orders_page: 'sales.purchase_orders.page.view',
      purchase_orders_order_creation: 'sales.purchase_orders.order_creation.view',
      purchase_orders_product_selection: 'sales.purchase_orders.product_selection.view',
      purchase_orders_po_creation: 'sales.purchase_orders.po_creation.view',
      purchase_orders_delivery_info: 'sales.purchase_orders.delivery_info.view',
      purchase_orders_review_info: 'sales.purchase_orders.review_info.view',
      purchase_orders_delivery_documents: 'sales.purchase_orders.delivery_documents.view',
      purchase_orders_inventory_usage: 'sales.purchase_orders.inventory_usage.view',
      purchase_orders_invoice_payment: 'sales.purchase_orders.invoice_payment.view',
      purchase_orders_delivery_details: 'sales.purchase_orders.delivery_details.view',
      purchase_orders_final_approval: 'sales.purchase_orders.final_approval.view',
      products: 'products.catalog.view',
      brands: 'products.brands.view',
      formulations: 'products.formulations.view',
      
      // Formulations detailed permissions
      formulations_overview: 'products.formulations.overview.view',
      formulations_ingredients: 'products.formulations.ingredients.view',
      formulations_procedures: 'products.formulations.procedures.view',
      formulations_tests: 'products.formulations.tests.view',
      formulations_costs: 'products.formulations.costs.view',
      formulations_summary: 'products.formulations.summary.view',

      locations: 'locations.locations.view',
      suppliers: 'supply_chain.suppliers.view',
      vendor_purchase_orders: 'supply_chain.vendor_purchase_orders.view',
      equipment_list: 'locations.equipment_list.view',
      equipment_maintenance: 'locations.equipment_maintenance.view',
      
      // Product Inventory detailed permissions
      product_inventory: 'inventory.products.overview.view',
      product_inventory_overview: 'inventory.products.overview.view',
      product_inventory_list: 'inventory.products.inventory.view',
      product_inventory_transactions: 'inventory.products.transactions.view',
      product_inventory_config: 'inventory.products.configurations.view',
      
      // Raw Materials detailed permissions
      raw_materials: 'inventory.raw_materials.overview.view',
      raw_materials_overview: 'inventory.raw_materials.overview.view',
      raw_materials_list: 'inventory.raw_materials.inventory.view',
      raw_materials_transactions: 'inventory.raw_materials.transactions.view',
      raw_materials_config: 'inventory.raw_materials.configurations.view',
      
      // Bottles detailed permissions
      bottles: 'inventory.bottles.overview.view',
      bottles_overview: 'inventory.bottles.overview.view',
      bottles_list: 'inventory.bottles.inventory.view',
      bottles_transactions: 'inventory.bottles.transactions.view',
      bottles_config: 'inventory.bottles.configurations.view',
      
      // Caps detailed permissions
      caps: 'inventory.caps.overview.view',
      caps_overview: 'inventory.caps.overview.view',
      caps_list: 'inventory.caps.inventory.view',
      caps_transactions: 'inventory.caps.transactions.view',
      caps_config: 'inventory.caps.configurations.view',
      
      // Labels detailed permissions
      labels: 'inventory.labels.overview.view',
      labels_overview: 'inventory.labels.overview.view',
      labels_list: 'inventory.labels.inventory.view',
      labels_transactions: 'inventory.labels.transactions.view',
      labels_config: 'inventory.labels.configurations.view',
      
      // Supplies detailed permissions
      supplies: 'inventory.supplies.overview.view',
      supplies_overview: 'inventory.supplies.overview.view',
      supplies_list: 'inventory.supplies.inventory.view',
      supplies_transactions: 'inventory.supplies.transactions.view',
      supplies_config: 'inventory.supplies.configurations.view',
      
      // All Inventory permissions
      inventory_page: 'inventory.inventory.view',
      inventory_transactions: 'inventory.inventory.transactions.view',
      inventory_configurations: 'inventory.inventory.configurations.view',

      // Imports and Exports permissions
      imports: 'inventory.imports.view',
      exports: 'inventory.exports.view',
      
      // Production detailed permissions
      production_overview: 'production.overview.view',
      production_calendar: 'production.calendar.view',
      production_batches: 'production.batches.view',
      batch_overview: 'production.batch_overview.view',
      batch_ingredients: 'production.batch_ingredients.view',
      batch_costs: 'production.batch_costs.view',
      batch_logs_overview: 'production.batch_logs.overview.view',
      batch_logs_premix: 'production.batch_logs.premix.view',
      batch_logs_ingredients: 'production.batch_logs.ingredients.view',
      batch_logs_qc_checks: 'production.batch_logs.qc_checks.view',
      batch_logs_deviations: 'production.batch_logs.deviations.view',
      batch_logs_yield: 'production.batch_logs.yield.view',
      batch_logs_signatures: 'production.batch_logs.signatures.view',
      batch_summary: 'production.batch_summary.view',
      production_batching: 'production.overview.view',
      assembly: 'production.assembly.view',
      batch_requests_manufacturing_orders: 'production.batch_requests.manufacturing_orders.view',
      
      // Line Production detailed permissions
      line_production_overview: 'production.line.overview.view',
      line_production_page: 'production.line.page.view',
      line_product_templates: 'production.line.templates.view',
      line_assembly_overview: 'production.line.assembly.overview.view',
      line_assembly_items: 'production.line.assembly.items.view',
      line_assembly_cost: 'production.line.assembly.cost.view',
      line_assembly_summary: 'production.line.assembly.summary.view',
      line_log_overview: 'production.line.log.overview.view',
      line_log_checklist: 'production.line.log.checklist.view',
      line_log_supply: 'production.line.log.supply.view',
      line_log_yield: 'production.line.log.yield.view',
      line_log_signatures: 'production.line.log.signatures.view',
      
      settings: 'settings.view',
      
      // Settings detailed permissions
      active_users: 'settings.user_management.active_users.view',
      invitations: 'settings.user_management.invitations.view',
      roles_permissions: 'settings.user_management.roles_permissions.view',
      company_information: 'settings.company_information.view',
      business_address: 'settings.business_address.view',
      contact_information: 'settings.contact_information.view',
      primary_contact: 'settings.primary_contact.view',
      business_settings: 'settings.business_settings.view',
      
      // Employees detailed permissions
      employees: 'employees.employees.view',
      employees_page: 'employees.employees.view',
      work_information: 'employees.work_information.view',
      resume: 'employees.resume.view',
      certifications: 'employees.certifications.view',
      personal_details: 'employees.personal_details.view',
      citizenship_identity: 'employees.citizenship_identity.view',
      
      // Expenses detailed permissions
      expenses: 'expenses.expenses.view',
      expenses_page: 'expenses.expenses.view',
      accounts: 'expenses.accounts.view',
      categories: 'expenses.categories.view'
    };

    // Special handling for inventory pages - check if user has access to any tab
    if (pageName === 'product_inventory') {
      return this.canAccessAnyProductInventoryTab();
    }
    if (pageName === 'formulations') {
      return this.hasPermission('products.formulations.view', 'read');
    }
    if (pageName === 'raw_materials') {
      return this.canAccessAnyRawMaterialsTab();
    }
    if (pageName === 'bottles') {
      return this.canAccessAnyBottlesTab();
    }
    if (pageName === 'caps') {
      return this.canAccessAnyCapsTab();
    }
    if (pageName === 'labels') {
      return this.canAccessAnyLabelsTab();
    }
    if (pageName === 'supplies') {
      return this.canAccessAnySuppliesTab();
    }

    const permission = pagePermissions[pageName];
    return permission ? this.hasPermission(permission) : false;
  }

  // Check if user can edit specific pages/components
  static canEditPage(pageName) {
    const pagePermissions = {
      calendar: 'calendar.edit',
      schedule: 'schedule.edit',
      customers: 'sales.customers.edit',
      invoices: 'sales.invoices.edit',
      price_lists: 'sales.price_lists.edit',
      purchase_orders_order_creation: 'sales.purchase_orders.order_creation.edit',
      purchase_orders_product_selection: 'sales.purchase_orders.product_selection.edit',
      purchase_orders_po_creation: 'sales.purchase_orders.po_creation.edit',
      purchase_orders_delivery_info: 'sales.purchase_orders.delivery_info.edit',
      purchase_orders_delivery_documents: 'sales.purchase_orders.delivery_documents.edit',
      purchase_orders_inventory_usage: 'sales.purchase_orders.inventory_usage.edit',
      purchase_orders_invoice_payment: 'sales.purchase_orders.invoice_payment.edit',
      purchase_orders_delivery_details: 'sales.purchase_orders.delivery_details.edit',
      products: 'products.catalog.edit',
      brands: 'products.brands.edit',
      formulations: 'products.formulations.edit',
      
      // Formulations detailed permissions
      formulations_overview: 'products.formulations.overview.edit',
      formulations_ingredients: 'products.formulations.ingredients.edit',
      formulations_procedures: 'products.formulations.procedures.edit',
      formulations_tests: 'products.formulations.tests.edit',
      formulations_costs: 'products.formulations.costs.edit',
      formulations_summary: 'products.formulations.summary.edit',
      locations: 'locations.locations.edit',
      suppliers: 'supply_chain.suppliers.edit',
      vendor_purchase_orders: 'supply_chain.vendor_purchase_orders.edit',
      equipment_list: 'locations.equipment_list.edit',
      equipment_maintenance: 'locations.equipment_maintenance.edit',
      
      // Product Inventory detailed permissions
      product_inventory: 'inventory.products.overview.edit',
      product_inventory_overview: 'inventory.products.overview.edit',
      product_inventory_list: 'inventory.products.inventory.edit',
      product_inventory_transactions: 'inventory.products.transactions.edit',
      product_inventory_config: 'inventory.products.configurations.edit',
      
      // Raw Materials detailed permissions
      raw_materials: 'inventory.raw_materials.overview.edit',
      raw_materials_overview: 'inventory.raw_materials.overview.edit',
      raw_materials_list: 'inventory.raw_materials.inventory.edit',
      raw_materials_transactions: 'inventory.raw_materials.transactions.edit',
      raw_materials_config: 'inventory.raw_materials.configurations.edit',
      
      // Bottles detailed permissions
      bottles: 'inventory.bottles.overview.edit',
      bottles_overview: 'inventory.bottles.overview.edit',
      bottles_list: 'inventory.bottles.inventory.edit',
      bottles_transactions: 'inventory.bottles.transactions.edit',
      bottles_config: 'inventory.bottles.configurations.edit',
      
      // Caps detailed permissions
      caps: 'inventory.caps.overview.edit',
      caps_overview: 'inventory.caps.overview.edit',
      caps_list: 'inventory.caps.inventory.edit',
      caps_transactions: 'inventory.caps.transactions.edit',
      caps_config: 'inventory.caps.configurations.edit',
      
      // Labels detailed permissions
      labels: 'inventory.labels.overview.edit',
      labels_overview: 'inventory.labels.overview.edit',
      labels_list: 'inventory.labels.inventory.edit',
      labels_transactions: 'inventory.labels.transactions.edit',
      labels_config: 'inventory.labels.configurations.edit',
      
      // Supplies detailed permissions
      supplies: 'inventory.supplies.overview.edit',
      supplies_overview: 'inventory.supplies.overview.edit',
      supplies_list: 'inventory.supplies.inventory.edit',
      supplies_transactions: 'inventory.supplies.transactions.edit',
      supplies_config: 'inventory.supplies.configurations.edit',
      
      // All Inventory permissions
      inventory_page: 'inventory.inventory.edit',
      inventory_transactions: 'inventory.inventory.transactions.edit',
      inventory_configurations: 'inventory.inventory.configurations.edit',

      // Imports and Exports permissions
      imports: 'inventory.imports.edit',
      exports: 'inventory.exports.edit',
      
      // Production detailed permissions
      production_overview: 'production.overview.edit',
      production_calendar: 'production.calendar.edit',
      production_batches: 'production.batches.edit',
      batch_overview: 'production.batch_overview.edit',
      batch_ingredients: 'production.batch_ingredients.edit',
      batch_costs: 'production.batch_costs.edit',
      batch_logs_overview: 'production.batch_logs.overview.edit',
      batch_logs_premix: 'production.batch_logs.premix.edit',
      batch_logs_ingredients: 'production.batch_logs.ingredients.edit',
      batch_logs_qc_checks: 'production.batch_logs.qc_checks.edit',
      batch_logs_deviations: 'production.batch_logs.deviations.edit',
      batch_logs_yield: 'production.batch_logs.yield.edit',
      batch_logs_signatures: 'production.batch_logs.signatures.edit',
      production_batching: 'production.overview.edit',
      assembly: 'production.assembly.edit',
      batch_requests_manufacturing_orders: 'production.batch_requests.manufacturing_orders.edit',
      
      // Line Production detailed permissions
      line_production_page: 'production.line.page.edit',
      line_product_templates: 'production.line.templates.edit',
      line_assembly_overview: 'production.line.assembly.overview.edit',
      line_assembly_items: 'production.line.assembly.items.edit',
      line_assembly_cost: 'production.line.assembly.cost.edit',
      line_log_overview: 'production.line.log.overview.edit',
      line_log_checklist: 'production.line.log.checklist.edit',
      line_log_supply: 'production.line.log.supply.edit',
      line_log_yield: 'production.line.log.yield.edit',
      line_log_signatures: 'production.line.log.signatures.edit',
      
      // Settings detailed permissions
      active_users: 'settings.user_management.active_users.edit',
      invitations: 'settings.user_management.invitations.invite',
      roles_permissions: 'settings.user_management.roles_permissions.edit',
      company_information: 'settings.company_information.edit',
      business_address: 'settings.business_address.edit',
      contact_information: 'settings.contact_information.edit',
      primary_contact: 'settings.primary_contact.edit',
      business_settings: 'settings.business_settings.edit',
      users: 'settings.user_management.active_users.edit',
      
      // Employees detailed permissions
      employees: 'employees.employees.edit',
      employees_page: 'employees.employees.edit',
      work_information: 'employees.work_information.edit',
      resume: 'employees.resume.edit',
      certifications: 'employees.certifications.edit',
      personal_details: 'employees.personal_details.edit',
      citizenship_identity: 'employees.citizenship_identity.edit',
      
      // Expenses detailed permissions
      expenses: 'expenses.expenses.edit',
      expenses_page: 'expenses.expenses.edit',
      accounts: 'expenses.accounts.edit',
      categories: 'expenses.categories.edit'
    };

    const permission = pagePermissions[pageName];
    return permission ? this.hasPermission(permission, 'write') : false;
  }

  // Check if user can delete from specific pages/components
  static canDeletePage(pageName) {
    const pagePermissions = {
      calendar: 'calendar.delete',
      schedule: 'schedule.delete',
      customers: 'sales.customers.delete',
      invoices: 'sales.invoices.delete',
      price_lists: 'sales.price_lists.delete',
      purchase_orders: 'sales.purchase_orders.delete',
      purchase_orders_product_selection: 'sales.purchase_orders.product_selection.delete',
      purchase_orders_po_creation: 'sales.purchase_orders.po_creation.delete',
      purchase_orders_delivery_info: 'sales.purchase_orders.delivery_info.delete',
      purchase_orders_delivery_documents: 'sales.purchase_orders.delivery_documents.delete',
      products: 'products.catalog.delete',
      brands: 'products.brands.delete',
      formulations: 'products.formulations.overview.delete',
      
      // Formulations detailed permissions
      formulations_overview: 'products.formulations.overview.delete',
      formulations_ingredients: 'products.formulations.ingredients.delete',
      formulations_procedures: 'products.formulations.procedures.delete',
      formulations_tests: 'products.formulations.tests.delete',
      formulations_costs: 'products.formulations.costs.delete',
      formulations_summary: 'products.formulations.summary.delete',
      locations: 'locations.locations.delete',
      suppliers: 'supply_chain.suppliers.delete',
      vendor_purchase_orders: 'supply_chain.vendor_purchase_orders.delete',
      equipment_list: 'locations.equipment_list.delete',
      equipment_maintenance: 'locations.equipment_maintenance.delete',
      
      // Product Inventory detailed permissions
      product_inventory: 'inventory.products.overview.delete',
      product_inventory_overview: 'inventory.products.overview.delete',
      product_inventory_list: 'inventory.products.inventory.delete',
      product_inventory_transactions: 'inventory.products.transactions.delete',
      product_inventory_config: 'inventory.products.configurations.delete',
      
      // Raw Materials detailed permissions
      raw_materials: 'inventory.raw_materials.overview.delete',
      raw_materials_overview: 'inventory.raw_materials.overview.delete',
      raw_materials_list: 'inventory.raw_materials.inventory.delete',
      raw_materials_transactions: 'inventory.raw_materials.transactions.delete',
      raw_materials_config: 'inventory.raw_materials.configurations.delete',
      
      // Bottles detailed permissions
      bottles: 'inventory.bottles.overview.delete',
      bottles_overview: 'inventory.bottles.overview.delete',
      bottles_list: 'inventory.bottles.inventory.delete',
      bottles_transactions: 'inventory.bottles.transactions.delete',
      bottles_config: 'inventory.bottles.configurations.delete',
      
      // Caps detailed permissions
      caps: 'inventory.caps.overview.delete',
      caps_overview: 'inventory.caps.overview.delete',
      caps_list: 'inventory.caps.inventory.delete',
      caps_transactions: 'inventory.caps.transactions.delete',
      caps_config: 'inventory.caps.configurations.delete',
      
      // Labels detailed permissions
      labels: 'inventory.labels.overview.delete',
      labels_overview: 'inventory.labels.overview.delete',
      labels_list: 'inventory.labels.inventory.delete',
      labels_transactions: 'inventory.labels.transactions.delete',
      labels_config: 'inventory.labels.configurations.delete',
      
      // Supplies detailed permissions
      supplies: 'inventory.supplies.overview.delete',
      supplies_overview: 'inventory.supplies.overview.delete',
      supplies_list: 'inventory.supplies.inventory.delete',
      supplies_transactions: 'inventory.supplies.transactions.delete',
      supplies_config: 'inventory.supplies.configurations.delete',
      
      // All Inventory permissions
      inventory_page: 'inventory.inventory.delete',
      inventory_transactions: 'inventory.inventory.transactions.delete',
      inventory_configurations: 'inventory.inventory.configurations.delete',

      // Imports and Exports permissions
      imports: 'inventory.imports.delete',
      exports: 'inventory.exports.delete',
      
      // Production detailed permissions
      production_overview: 'production.overview.delete',
      production_calendar: 'production.calendar.delete',
      batch_overview: 'production.batch_overview.delete',
      batch_ingredients: 'production.batch_ingredients.delete',
      batch_costs: 'production.batch_costs.delete',
      batch_logs_overview: 'production.batch_logs.overview.delete',
      batch_logs_premix: 'production.batch_logs.premix.delete',
      batch_logs_ingredients: 'production.batch_logs.ingredients.delete',
      batch_logs_qc_checks: 'production.batch_logs.qc_checks.delete',
      batch_logs_deviations: 'production.batch_logs.deviations.delete',
      batch_logs_yield: 'production.batch_logs.yield.delete',
      production_batching: 'production.overview.delete',
      assembly: 'production.assembly.delete',
      batch_requests_manufacturing_orders: 'production.batch_requests.manufacturing_orders.delete',
      
      // Line Production detailed permissions
      line_product_templates: 'production.line.templates.delete',
      line_assembly_items: 'production.line.assembly.items.delete',
      line_log_supply: 'production.line.log.supply.delete',
      
      // Settings detailed permissions
      active_users: 'settings.user_management.active_users.complete_removal',
      invitations: 'settings.user_management.invitations.invite',
      roles_permissions: 'settings.user_management.roles_permissions.delete',
      company_information: 'settings.company_information.delete',
      business_address: 'settings.business_address.delete',
      contact_information: 'settings.contact_information.delete',
      primary_contact: 'settings.primary_contact.delete',
      business_settings: 'settings.business_settings.delete',
      users: 'settings.user_management.active_users.complete_removal',
      
      // Employees detailed permissions
      employees: 'employees.employees.delete',
      employees_page: 'employees.employees.delete',
      work_information: 'employees.work_information.delete',
      resume: 'employees.resume.delete',
      certifications: 'employees.certifications.delete',
      personal_details: 'employees.personal_details.delete',
      // Note: citizenship_identity has no delete permission as per requirements
      
      // Expenses detailed permissions
      expenses: 'expenses.expenses.delete',
      expenses_page: 'expenses.expenses.delete',
      accounts: 'expenses.accounts.delete',
      categories: 'expenses.categories.delete'
    };

    const permission = pagePermissions[pageName];
    return permission ? this.hasPermission(permission, 'delete') : false;
  }

  // Check if user can create formulations
  static canCreateFormulations() {
    return this.hasPermission('products.formulations.create', 'write');
  }

  // Check if user can approve formulations
  static canApproveFormulations() {
    return this.hasPermission('products.formulations.approve', 'write');
  }

  // Check if user can permanently delete formulations
  static canPermanentlyDeleteFormulations() {
    return this.hasPermission('products.formulations.delete_permanent', 'delete');
  }

  // Check if user can deactivate users (respects hierarchy)
  static canDeactivateUsers() {
    return this.hasPermission('settings.user_management.active_users.deactivate', 'write');
  }

  // Check if user can completely remove users (respects hierarchy)
  static canCompletelyRemoveUsers() {
    return this.hasPermission('settings.user_management.active_users.complete_removal', 'delete');
  }

  // Check if user can invite users
  static canInviteUsers() {
    return this.hasPermission('settings.user_management.invitations.invite', 'write');
  }

  // Check if user can create production batches
  static canCreateProductionBatches() {
    return this.hasPermission('production.batches.create', 'write');
  }

  // Check if user can approve production batches
  static canApproveProductionBatches() {
    return this.hasPermission('production.batch_summary.approve', 'write');
  }

  // Check if user can permanently delete production batches
  static canPermanentlyDeleteProductionBatches() {
    return this.hasPermission('production.batches.delete_permanent', 'delete');
  }

  // Check if user can approve QC tests
  static canApproveQCTests() {
    return this.hasPermission('production.batch_logs.qc_checks.approve', 'write');
  }

  // Check if user can close deviation issues
  static canCloseDeviationIssues() {
    return this.hasPermission('production.batch_logs.deviations.close_issue', 'write');
  }

  // Check if user can start CAPA investigations
  static canStartCAPAInvestigations() {
    return this.hasPermission('production.batch_logs.deviations.start_capa', 'write');
  }

  // Check if user can close investigations
  static canCloseInvestigations() {
    return this.hasPermission('production.batch_logs.deviations.close_investigation', 'write');
  }

  // Check if user can provide production supervisor signature
  static canProvideProductionSupervisorSignature() {
    return this.hasPermission('production.batch_logs.signatures.supervisor', 'write');
  }

  // Check if user can provide quality control signature
  static canProvideQualityControlSignature() {
    return this.hasPermission('production.batch_logs.signatures.quality_control', 'write');
  }

  // Check if user can provide final release signature
  static canProvideFinalReleaseSignature() {
    return this.hasPermission('production.batch_logs.signatures.final_release', 'write');
  }

  // Check if user can approve manufacturing orders
  static canApproveManufacturingOrders() {
    return this.hasPermission('production.batch_requests.manufacturing_orders.approval', 'write');
  }

  // Employee permission checking methods
  static canViewEmployees() {
    return this.hasPermission('employees.employees.view', 'read');
  }

  static canEditEmployees() {
    return this.hasPermission('employees.employees.edit', 'write');
  }

  static canDeleteEmployees() {
    return this.hasPermission('employees.employees.delete', 'delete');
  }

  static canViewWorkInformation() {
    return this.hasPermission('employees.work_information.view', 'read');
  }

  static canEditWorkInformation() {
    return this.hasPermission('employees.work_information.edit', 'write');
  }

  static canDeleteWorkInformation() {
    return this.hasPermission('employees.work_information.delete', 'delete');
  }

  static canViewResume() {
    return this.hasPermission('employees.resume.view', 'read');
  }

  static canEditResume() {
    return this.hasPermission('employees.resume.edit', 'write');
  }

  static canDeleteResume() {
    return this.hasPermission('employees.resume.delete', 'delete');
  }

  static canViewCertifications() {
    return this.hasPermission('employees.certifications.view', 'read');
  }

  static canEditCertifications() {
    return this.hasPermission('employees.certifications.edit', 'write');
  }

  static canDeleteCertifications() {
    return this.hasPermission('employees.certifications.delete', 'delete');
  }

  static canViewPersonalDetails() {
    return this.hasPermission('employees.personal_details.view', 'read');
  }

  static canEditPersonalDetails() {
    return this.hasPermission('employees.personal_details.edit', 'write');
  }

  static canDeletePersonalDetails() {
    return this.hasPermission('employees.personal_details.delete', 'delete');
  }

  static canViewCitizenshipIdentity() {
    return this.hasPermission('employees.citizenship_identity.view', 'read');
  }

  static canEditCitizenshipIdentity() {
    return this.hasPermission('employees.citizenship_identity.edit', 'write');
  }

  // Material Quality Control permissions - Supply Quality Check
  static canViewSupplyQCTests() {
    return this.hasPermission('inventory.supplies.qc_tests.view', 'read');
  }

  static canEditSupplyQCTests() {
    return this.hasPermission('inventory.supplies.qc_tests.edit', 'write');
  }

  static canDeleteSupplyQCTests() {
    return this.hasPermission('inventory.supplies.qc_tests.delete', 'delete');
  }

  static canApproveSupplyQCTests() {
    return this.hasPermission('inventory.supplies.qc_tests.approve', 'write');
  }

  // Material Quality Control permissions - Raw Material Quality Check
  static canViewRawMaterialQCTests() {
    return this.hasPermission('inventory.raw_materials.qc_tests.view', 'read');
  }

  static canEditRawMaterialQCTests() {
    return this.hasPermission('inventory.raw_materials.qc_tests.edit', 'write');
  }

  static canDeleteRawMaterialQCTests() {
    return this.hasPermission('inventory.raw_materials.qc_tests.delete', 'delete');
  }

  static canApproveRawMaterialQCTests() {
    return this.hasPermission('inventory.raw_materials.qc_tests.approve', 'write');
  }

  // Line Production special permissions
  static canCreateLine() {
    return this.hasPermission('production.line.create', 'write');
  }

  static canDeleteLine() {
    return this.hasPermission('production.line.delete', 'delete');
  }

  // Line Production Overview permissions
  static canViewLineProductionOverview() {
    return this.hasPermission('production.line.overview.view', 'read');
  }

  // Line Production Page permissions
  static canViewLineProductionPage() {
    return this.hasPermission('production.line.page.view', 'read');
  }

  static canEditLineProductionPage() {
    return this.hasPermission('production.line.page.edit', 'write');
  }

  // Line Production - Product Templates permissions
  static canViewLineProductTemplates() {
    return this.hasPermission('production.line.templates.view', 'read');
  }

  static canEditLineProductTemplates() {
    return this.hasPermission('production.line.templates.edit', 'write');
  }

  static canDeleteLineProductTemplates() {
    return this.hasPermission('production.line.templates.delete', 'delete');
  }

  // Line Assembly - Line Overview permissions
  static canViewLineOverview() {
    return this.hasPermission('production.line.assembly.overview.view', 'read');
  }

  static canEditLineOverview() {
    return this.hasPermission('production.line.assembly.overview.edit', 'write');
  }

  // Line Assembly - Line Items permissions
  static canViewLineItems() {
    return this.hasPermission('production.line.assembly.items.view', 'read');
  }

  static canEditLineItems() {
    return this.hasPermission('production.line.assembly.items.edit', 'write');
  }

  static canDeleteLineItems() {
    return this.hasPermission('production.line.assembly.items.delete', 'delete');
  }

  // Line Assembly - Line Cost permissions
  static canViewLineCost() {
    return this.hasPermission('production.line.assembly.cost.view', 'read');
  }

  static canEditLineCost() {
    return this.hasPermission('production.line.assembly.cost.edit', 'write');
  }

  // Line Assembly - Line Summary permissions
  static canViewLineSummary() {
    return this.hasPermission('production.line.assembly.summary.view', 'read');
  }

  static canApproveLineProduction() {
    return this.hasPermission('production.line.assembly.summary.approve', 'write');
  }

  // Line Log - Overview Log permissions
  static canViewLineLogOverview() {
    return this.hasPermission('production.line.log.overview.view', 'read');
  }

  static canEditLineLogOverview() {
    return this.hasPermission('production.line.log.overview.edit', 'write');
  }

  // Line Log - Area Checklist Log permissions
  static canViewLineLogChecklist() {
    return this.hasPermission('production.line.log.checklist.view', 'read');
  }

  static canEditLineLogChecklist() {
    return this.hasPermission('production.line.log.checklist.edit', 'write');
  }

  // Line Log - Supply Log permissions
  static canViewLineLogSupply() {
    return this.hasPermission('production.line.log.supply.view', 'read');
  }

  static canEditLineLogSupply() {
    return this.hasPermission('production.line.log.supply.edit', 'write');
  }

  static canDeleteLineLogSupply() {
    return this.hasPermission('production.line.log.supply.delete', 'delete');
  }

  // Line Log - Yield Log permissions
  static canViewLineLogYield() {
    return this.hasPermission('production.line.log.yield.view', 'read');
  }

  static canEditLineLogYield() {
    return this.hasPermission('production.line.log.yield.edit', 'write');
  }

  // Line Log - Signatures Log permissions
  static canViewLineLogSignatures() {
    return this.hasPermission('production.line.log.signatures.view', 'read');
  }

  static canEditLineLogSignatures() {
    return this.hasPermission('production.line.log.signatures.edit', 'write');
  }

  static canProvideLineProductionSupervisorSignature() {
    return this.hasPermission('production.line.log.signatures.supervisor', 'write');
  }

  static canProvideLineQualityControlSignature() {
    return this.hasPermission('production.line.log.signatures.quality_control', 'write');
  }

  static canProvideLineFinalReleaseSignature() {
    return this.hasPermission('production.line.log.signatures.final_release', 'write');
  }

  // Equipment List permissions
  static canViewEquipmentList() {
    return this.hasPermission('locations.equipment_list.view', 'read');
  }

  static canEditEquipmentList() {
    return this.hasPermission('locations.equipment_list.edit', 'write');
  }

  static canDeleteEquipmentList() {
    return this.hasPermission('locations.equipment_list.delete', 'delete');
  }

  // Equipment Maintenance permissions
  static canViewEquipmentMaintenance() {
    return this.hasPermission('locations.equipment_maintenance.view', 'read');
  }

  static canEditEquipmentMaintenance() {
    return this.hasPermission('locations.equipment_maintenance.edit', 'write');
  }

  static canDeleteEquipmentMaintenance() {
    return this.hasPermission('locations.equipment_maintenance.delete', 'delete');
  }

  static canApproveMaintenance() {
    return this.hasPermission('locations.equipment_maintenance.approve', 'write');
  }

  // All Inventory permissions
  static canViewInventoryPage() {
    return this.hasPermission('inventory.inventory.view', 'read');
  }

  static canEditInventoryPage() {
    return this.hasPermission('inventory.inventory.edit', 'write');
  }

  static canDeleteInventoryPage() {
    return this.hasPermission('inventory.inventory.delete', 'delete');
  }

  static canViewInventoryTransactions() {
    return this.hasPermission('inventory.inventory.transactions.view', 'read');
  }

  static canEditInventoryTransactions() {
    return this.hasPermission('inventory.inventory.transactions.edit', 'write');
  }

  static canDeleteInventoryTransactions() {
    return this.hasPermission('inventory.inventory.transactions.delete', 'delete');
  }

  static canViewInventoryConfigurations() {
    return this.hasPermission('inventory.inventory.configurations.view', 'read');
  }

  static canEditInventoryConfigurations() {
    return this.hasPermission('inventory.inventory.configurations.edit', 'write');
  }

  static canDeleteInventoryConfigurations() {
    return this.hasPermission('inventory.inventory.configurations.delete', 'delete');
  }

  // Imports permissions
  static canViewImports() {
    return this.hasPermission('inventory.imports.view', 'read');
  }

  static canEditImports() {
    return this.hasPermission('inventory.imports.edit', 'write');
  }

  static canDeleteImports() {
    return this.hasPermission('inventory.imports.delete', 'delete');
  }

  static canApproveImports() {
    return this.hasPermission('inventory.imports.approve', 'write');
  }

  // Exports permissions
  static canViewExports() {
    return this.hasPermission('inventory.exports.view', 'read');
  }

  static canEditExports() {
    return this.hasPermission('inventory.exports.edit', 'write');
  }

  static canDeleteExports() {
    return this.hasPermission('inventory.exports.delete', 'delete');
  }

  static canApproveExports() {
    return this.hasPermission('inventory.exports.approve', 'write');
  }

  // Expenses permissions
  static canViewExpenses() {
    return this.hasPermission('expenses.expenses.view', 'read');
  }

  static canEditExpenses() {
    return this.hasPermission('expenses.expenses.edit', 'write');
  }

  static canDeleteExpenses() {
    return this.hasPermission('expenses.expenses.delete', 'delete');
  }

  static canApproveExpenses() {
    return this.hasPermission('expenses.expenses.approve', 'write');
  }

  static canViewAccounts() {
    return this.hasPermission('expenses.accounts.view', 'read');
  }

  static canEditAccounts() {
    return this.hasPermission('expenses.accounts.edit', 'write');
  }

  static canDeleteAccounts() {
    return this.hasPermission('expenses.accounts.delete', 'delete');
  }

  static canViewCategories() {
    return this.hasPermission('expenses.categories.view', 'read');
  }

  static canEditCategories() {
    return this.hasPermission('expenses.categories.edit', 'write');
  }

  static canDeleteCategories() {
    return this.hasPermission('expenses.categories.delete', 'delete');
  }

  // Vendor Purchase Orders permissions
  static canViewVendorPurchaseOrders() {
    return this.hasPermission('supply_chain.vendor_purchase_orders.view', 'read');
  }

  static canEditVendorPurchaseOrders() {
    return this.hasPermission('supply_chain.vendor_purchase_orders.edit', 'write');
  }

  static canDeleteVendorPurchaseOrders() {
    return this.hasPermission('supply_chain.vendor_purchase_orders.delete', 'delete');
  }

  // Accounting permissions
  static canViewAccountingOverview() {
    return this.hasPermission('accounting.overview.view', 'read');
  }

  static canEditAccountingOverview() {
    return this.hasPermission('accounting.overview.edit', 'write');
  }

  static canDeleteAccountingOverview() {
    return this.hasPermission('accounting.overview.delete', 'delete');
  }

  static canViewAccountingBanking() {
    return this.hasPermission('accounting.banking.view', 'read');
  }

  static canEditAccountingBanking() {
    return this.hasPermission('accounting.banking.edit', 'write');
  }

  static canDeleteAccountingBanking() {
    return this.hasPermission('accounting.banking.delete', 'delete');
  }

  static canViewAccountingReceivables() {
    return this.hasPermission('accounting.receivables.view', 'read');
  }

  static canEditAccountingReceivables() {
    return this.hasPermission('accounting.receivables.edit', 'write');
  }

  static canDeleteAccountingReceivables() {
    return this.hasPermission('accounting.receivables.delete', 'delete');
  }

  static canViewAccountingPayables() {
    return this.hasPermission('accounting.payables.view', 'read');
  }

  static canEditAccountingPayables() {
    return this.hasPermission('accounting.payables.edit', 'write');
  }

  static canDeleteAccountingPayables() {
    return this.hasPermission('accounting.payables.delete', 'delete');
  }

  static canViewAccountingForecast() {
    return this.hasPermission('accounting.forecast.view', 'read');
  }

  static canEditAccountingForecast() {
    return this.hasPermission('accounting.forecast.edit', 'write');
  }

  static canDeleteAccountingForecast() {
    return this.hasPermission('accounting.forecast.delete', 'delete');
  }

  // Purchase Orders permissions
  static canCreatePurchaseOrders() {
    return this.hasPermission('sales.purchase_orders.create', 'write');
  }

  static canDeletePurchaseOrders() {
    return this.hasPermission('sales.purchase_orders.delete', 'delete');
  }

  static canViewPurchaseOrdersPage() {
    return this.hasPermission('sales.purchase_orders.page.view', 'read');
  }

  // Purchase Orders - Order Creation
  static canViewOrderCreation() {
    return this.hasPermission('sales.purchase_orders.order_creation.view', 'read');
  }

  static canEditOrderCreation() {
    return this.hasPermission('sales.purchase_orders.order_creation.edit', 'write');
  }

  // Purchase Orders - Product Selection
  static canViewProductSelection() {
    return this.hasPermission('sales.purchase_orders.product_selection.view', 'read');
  }

  static canEditProductSelection() {
    return this.hasPermission('sales.purchase_orders.product_selection.edit', 'write');
  }

  static canDeleteProductSelection() {
    return this.hasPermission('sales.purchase_orders.product_selection.delete', 'delete');
  }

  static canApproveProductSelection() {
    return this.hasPermission('sales.purchase_orders.product_selection.approve', 'write');
  }

  // Purchase Orders - PO Creation
  static canViewPOCreation() {
    return this.hasPermission('sales.purchase_orders.po_creation.view', 'read');
  }

  static canEditPOCreation() {
    return this.hasPermission('sales.purchase_orders.po_creation.edit', 'write');
  }

  static canDeletePOCreation() {
    return this.hasPermission('sales.purchase_orders.po_creation.delete', 'delete');
  }

  // Purchase Orders - Delivery Info
  static canViewDeliveryInfo() {
    return this.hasPermission('sales.purchase_orders.delivery_info.view', 'read');
  }

  static canEditDeliveryInfo() {
    return this.hasPermission('sales.purchase_orders.delivery_info.edit', 'write');
  }

  static canDeleteDeliveryInfo() {
    return this.hasPermission('sales.purchase_orders.delivery_info.delete', 'delete');
  }

  // Purchase Orders - Review Info
  static canViewReviewInfo() {
    return this.hasPermission('sales.purchase_orders.review_info.view', 'read');
  }

  static canApproveReviewInfo() {
    return this.hasPermission('sales.purchase_orders.review_info.approve', 'write');
  }

  // Purchase Orders - Delivery Documents
  static canViewDeliveryDocuments() {
    return this.hasPermission('sales.purchase_orders.delivery_documents.view', 'read');
  }

  static canEditDeliveryDocuments() {
    return this.hasPermission('sales.purchase_orders.delivery_documents.edit', 'write');
  }

  static canDeleteDeliveryDocuments() {
    return this.hasPermission('sales.purchase_orders.delivery_documents.delete', 'delete');
  }

  // Purchase Orders - Inventory Usage
  static canViewInventoryUsage() {
    return this.hasPermission('sales.purchase_orders.inventory_usage.view', 'read');
  }

  static canEditInventoryUsage() {
    return this.hasPermission('sales.purchase_orders.inventory_usage.edit', 'write');
  }

  // Purchase Orders - Invoice & Payment
  static canViewInvoicePayment() {
    return this.hasPermission('sales.purchase_orders.invoice_payment.view', 'read');
  }

  static canEditInvoicePayment() {
    return this.hasPermission('sales.purchase_orders.invoice_payment.edit', 'write');
  }

  // Purchase Orders - Delivery Details
  static canViewDeliveryDetails() {
    return this.hasPermission('sales.purchase_orders.delivery_details.view', 'read');
  }

  static canEditDeliveryDetails() {
    return this.hasPermission('sales.purchase_orders.delivery_details.edit', 'write');
  }

  // Purchase Orders - Final Approval
  static canViewFinalApproval() {
    return this.hasPermission('sales.purchase_orders.final_approval.view', 'read');
  }

  static canApproveFinalDelivery() {
    return this.hasPermission('sales.purchase_orders.final_approval.approve', 'write');
  }

  // Clear user context (for logout)
  static clearContext() {
    this.userCompanyContext = null;
    this.userPermissions.clear();
  }

  // Refresh user context and permissions
  static async refresh() {
    await this.initializeUserContext();
  }

  // Clear all cached permission data (security fix)
  static clearUserContext() {
    this.userCompanyContext = null;
    this.userPermissions = new Map();
    localStorage.removeItem('currentCompanyId');
    localStorage.removeItem('userPermissions');
  }
}

// React hook for permission checking
export function usePermissions() {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const initializePermissions = async () => {
    setLoading(true);
    await PermissionManager.initializeUserContext();
    setPermissions(PermissionManager.userCompanyContext);
    setLoading(false);
  };

  useEffect(() => {
    initializePermissions();
  }, []);

  const hasPermission = (permissionName, accessType = 'read') => {
    return PermissionManager.hasPermission(permissionName, accessType);
  };

  const canAccessPage = (pageName) => {
    return PermissionManager.canAccessPage(pageName);
  };

  const canEditPage = (pageName) => {
    return PermissionManager.canEditPage(pageName);
  };

  const canAccessSettings = () => {
    return PermissionManager.canAccessSettings();
  };

  const canAccessAnyProductInventoryTab = () => {
    return PermissionManager.canAccessAnyProductInventoryTab();
  };

  const canAccessAnyRawMaterialsTab = () => {
    return PermissionManager.canAccessAnyRawMaterialsTab();
  };

  const canAccessAnyBottlesTab = () => {
    return PermissionManager.canAccessAnyBottlesTab();
  };

  const canAccessAnyCapsTab = () => {
    return PermissionManager.canAccessAnyCapsTab();
  };

  const canAccessAnyLabelsTab = () => {
    return PermissionManager.canAccessAnyLabelsTab();
  };

  const canAccessAnySuppliesTab = () => {
    return PermissionManager.canAccessAnySuppliesTab();
  };

  const refresh = async () => {
    await initializePermissions();
  };

  return {
    permissions,
    loading,
    hasPermission,
    canAccessPage,
    canEditPage,
    canAccessSettings,
    canAccessAnyProductInventoryTab,
    canAccessAnyFormulationsTab: () => PermissionManager.canAccessAnyFormulationsTab(),
    canAccessAnyRawMaterialsTab,
    canAccessAnyBottlesTab,
    canAccessAnyCapsTab,
    canAccessAnyLabelsTab,
    canAccessAnySuppliesTab,
    canDeletePage: (pageName) => PermissionManager.canDeletePage(pageName),
    canCreateFormulations: () => PermissionManager.canCreateFormulations(),
    canApproveFormulations: () => PermissionManager.canApproveFormulations(),
    canPermanentlyDeleteFormulations: () => PermissionManager.canPermanentlyDeleteFormulations(),
    canDeactivateUsers: () => PermissionManager.canDeactivateUsers(),
    canCompletelyRemoveUsers: () => PermissionManager.canCompletelyRemoveUsers(),
    canInviteUsers: () => PermissionManager.canInviteUsers(),
    canCreateProductionBatches: () => PermissionManager.canCreateProductionBatches(),
    canApproveProductionBatches: () => PermissionManager.canApproveProductionBatches(),
    canPermanentlyDeleteProductionBatches: () => PermissionManager.canPermanentlyDeleteProductionBatches(),
    canApproveQCTests: () => PermissionManager.canApproveQCTests(),
    canCloseDeviationIssues: () => PermissionManager.canCloseDeviationIssues(),
    canStartCAPAInvestigations: () => PermissionManager.canStartCAPAInvestigations(),
    canCloseInvestigations: () => PermissionManager.canCloseInvestigations(),
    canProvideProductionSupervisorSignature: () => PermissionManager.canProvideProductionSupervisorSignature(),
    canProvideQualityControlSignature: () => PermissionManager.canProvideQualityControlSignature(),
    canProvideFinalReleaseSignature: () => PermissionManager.canProvideFinalReleaseSignature(),
    canApproveManufacturingOrders: () => PermissionManager.canApproveManufacturingOrders(),
    // Employee permission methods
    canViewEmployees: () => PermissionManager.canViewEmployees(),
    canEditEmployees: () => PermissionManager.canEditEmployees(),
    canDeleteEmployees: () => PermissionManager.canDeleteEmployees(),
    canViewWorkInformation: () => PermissionManager.canViewWorkInformation(),
    canEditWorkInformation: () => PermissionManager.canEditWorkInformation(),
    canDeleteWorkInformation: () => PermissionManager.canDeleteWorkInformation(),
    canViewResume: () => PermissionManager.canViewResume(),
    canEditResume: () => PermissionManager.canEditResume(),
    canDeleteResume: () => PermissionManager.canDeleteResume(),
    canViewCertifications: () => PermissionManager.canViewCertifications(),
    canEditCertifications: () => PermissionManager.canEditCertifications(),
    canDeleteCertifications: () => PermissionManager.canDeleteCertifications(),
    canViewPersonalDetails: () => PermissionManager.canViewPersonalDetails(),
    canEditPersonalDetails: () => PermissionManager.canEditPersonalDetails(),
    canDeletePersonalDetails: () => PermissionManager.canDeletePersonalDetails(),
    canViewCitizenshipIdentity: () => PermissionManager.canViewCitizenshipIdentity(),
    canEditCitizenshipIdentity: () => PermissionManager.canEditCitizenshipIdentity(),
    // Material Quality Control permission methods
    canViewSupplyQCTests: () => PermissionManager.canViewSupplyQCTests(),
    canEditSupplyQCTests: () => PermissionManager.canEditSupplyQCTests(),
    canDeleteSupplyQCTests: () => PermissionManager.canDeleteSupplyQCTests(),
    canApproveSupplyQCTests: () => PermissionManager.canApproveSupplyQCTests(),
    canViewRawMaterialQCTests: () => PermissionManager.canViewRawMaterialQCTests(),
    canEditRawMaterialQCTests: () => PermissionManager.canEditRawMaterialQCTests(),
    canDeleteRawMaterialQCTests: () => PermissionManager.canDeleteRawMaterialQCTests(),
    canApproveRawMaterialQCTests: () => PermissionManager.canApproveRawMaterialQCTests(),
    // Line Production permission methods
    canCreateLine: () => PermissionManager.canCreateLine(),
    canDeleteLine: () => PermissionManager.canDeleteLine(),
    canViewLineProductionOverview: () => PermissionManager.canViewLineProductionOverview(),
    canViewLineProductionPage: () => PermissionManager.canViewLineProductionPage(),
    canEditLineProductionPage: () => PermissionManager.canEditLineProductionPage(),
    canViewLineProductTemplates: () => PermissionManager.canViewLineProductTemplates(),
    canEditLineProductTemplates: () => PermissionManager.canEditLineProductTemplates(),
    canDeleteLineProductTemplates: () => PermissionManager.canDeleteLineProductTemplates(),
    canViewLineOverview: () => PermissionManager.canViewLineOverview(),
    canEditLineOverview: () => PermissionManager.canEditLineOverview(),
    canViewLineItems: () => PermissionManager.canViewLineItems(),
    canEditLineItems: () => PermissionManager.canEditLineItems(),
    canDeleteLineItems: () => PermissionManager.canDeleteLineItems(),
    canViewLineCost: () => PermissionManager.canViewLineCost(),
    canEditLineCost: () => PermissionManager.canEditLineCost(),
    canViewLineSummary: () => PermissionManager.canViewLineSummary(),
    canApproveLineProduction: () => PermissionManager.canApproveLineProduction(),
    canViewLineLogOverview: () => PermissionManager.canViewLineLogOverview(),
    canEditLineLogOverview: () => PermissionManager.canEditLineLogOverview(),
    canViewLineLogChecklist: () => PermissionManager.canViewLineLogChecklist(),
    canEditLineLogChecklist: () => PermissionManager.canEditLineLogChecklist(),
    canViewLineLogSupply: () => PermissionManager.canViewLineLogSupply(),
    canEditLineLogSupply: () => PermissionManager.canEditLineLogSupply(),
    canDeleteLineLogSupply: () => PermissionManager.canDeleteLineLogSupply(),
    canViewLineLogYield: () => PermissionManager.canViewLineLogYield(),
    canEditLineLogYield: () => PermissionManager.canEditLineLogYield(),
    canViewLineLogSignatures: () => PermissionManager.canViewLineLogSignatures(),
    canEditLineLogSignatures: () => PermissionManager.canEditLineLogSignatures(),
    canProvideLineProductionSupervisorSignature: () => PermissionManager.canProvideLineProductionSupervisorSignature(),
    canProvideLineQualityControlSignature: () => PermissionManager.canProvideLineQualityControlSignature(),
    canProvideLineFinalReleaseSignature: () => PermissionManager.canProvideLineFinalReleaseSignature(),
    // Equipment List permission methods
    canViewEquipmentList: () => PermissionManager.canViewEquipmentList(),
    canEditEquipmentList: () => PermissionManager.canEditEquipmentList(),
    canDeleteEquipmentList: () => PermissionManager.canDeleteEquipmentList(),
    // Equipment Maintenance permission methods
    canViewEquipmentMaintenance: () => PermissionManager.canViewEquipmentMaintenance(),
    canEditEquipmentMaintenance: () => PermissionManager.canEditEquipmentMaintenance(),
    canDeleteEquipmentMaintenance: () => PermissionManager.canDeleteEquipmentMaintenance(),
    canApproveMaintenance: () => PermissionManager.canApproveMaintenance(),
    // All Inventory permission methods
    canViewInventoryPage: () => PermissionManager.canViewInventoryPage(),
    canEditInventoryPage: () => PermissionManager.canEditInventoryPage(),
    canDeleteInventoryPage: () => PermissionManager.canDeleteInventoryPage(),
    canViewInventoryTransactions: () => PermissionManager.canViewInventoryTransactions(),
    canEditInventoryTransactions: () => PermissionManager.canEditInventoryTransactions(),
    canDeleteInventoryTransactions: () => PermissionManager.canDeleteInventoryTransactions(),
    canViewInventoryConfigurations: () => PermissionManager.canViewInventoryConfigurations(),
    canEditInventoryConfigurations: () => PermissionManager.canEditInventoryConfigurations(),
    canDeleteInventoryConfigurations: () => PermissionManager.canDeleteInventoryConfigurations(),
    // Imports permission methods
    canViewImports: () => PermissionManager.canViewImports(),
    canEditImports: () => PermissionManager.canEditImports(),
    canDeleteImports: () => PermissionManager.canDeleteImports(),
    canApproveImports: () => PermissionManager.canApproveImports(),
    // Exports permission methods
    canViewExports: () => PermissionManager.canViewExports(),
    canEditExports: () => PermissionManager.canEditExports(),
    canDeleteExports: () => PermissionManager.canDeleteExports(),
    canApproveExports: () => PermissionManager.canApproveExports(),
    // Expenses permission methods
    canViewExpenses: () => PermissionManager.canViewExpenses(),
    canEditExpenses: () => PermissionManager.canEditExpenses(),
    canDeleteExpenses: () => PermissionManager.canDeleteExpenses(),
    canApproveExpenses: () => PermissionManager.canApproveExpenses(),
    canViewAccounts: () => PermissionManager.canViewAccounts(),
    canEditAccounts: () => PermissionManager.canEditAccounts(),
    canDeleteAccounts: () => PermissionManager.canDeleteAccounts(),
    canViewCategories: () => PermissionManager.canViewCategories(),
    canEditCategories: () => PermissionManager.canEditCategories(),
    canDeleteCategories: () => PermissionManager.canDeleteCategories(),
    // Vendor Purchase Orders permission methods
    canViewVendorPurchaseOrders: () => PermissionManager.canViewVendorPurchaseOrders(),
    canEditVendorPurchaseOrders: () => PermissionManager.canEditVendorPurchaseOrders(),
    canDeleteVendorPurchaseOrders: () => PermissionManager.canDeleteVendorPurchaseOrders(),
    
    // Accounting permissions
    canViewAccountingOverview: () => PermissionManager.canViewAccountingOverview(),
    canEditAccountingOverview: () => PermissionManager.canEditAccountingOverview(),
    canDeleteAccountingOverview: () => PermissionManager.canDeleteAccountingOverview(),
    canViewAccountingBanking: () => PermissionManager.canViewAccountingBanking(),
    canEditAccountingBanking: () => PermissionManager.canEditAccountingBanking(),
    canDeleteAccountingBanking: () => PermissionManager.canDeleteAccountingBanking(),
    canViewAccountingReceivables: () => PermissionManager.canViewAccountingReceivables(),
    canEditAccountingReceivables: () => PermissionManager.canEditAccountingReceivables(),
    canDeleteAccountingReceivables: () => PermissionManager.canDeleteAccountingReceivables(),
    canViewAccountingPayables: () => PermissionManager.canViewAccountingPayables(),
    canEditAccountingPayables: () => PermissionManager.canEditAccountingPayables(),
    canDeleteAccountingPayables: () => PermissionManager.canDeleteAccountingPayables(),
    canViewAccountingForecast: () => PermissionManager.canViewAccountingForecast(),
    canEditAccountingForecast: () => PermissionManager.canEditAccountingForecast(),
    canDeleteAccountingForecast: () => PermissionManager.canDeleteAccountingForecast(),
    // Purchase Orders permission methods
    canCreatePurchaseOrders: () => PermissionManager.canCreatePurchaseOrders(),
    canDeletePurchaseOrders: () => PermissionManager.canDeletePurchaseOrders(),
    canViewPurchaseOrdersPage: () => PermissionManager.canViewPurchaseOrdersPage(),
    canViewOrderCreation: () => PermissionManager.canViewOrderCreation(),
    canEditOrderCreation: () => PermissionManager.canEditOrderCreation(),
    canViewProductSelection: () => PermissionManager.canViewProductSelection(),
    canEditProductSelection: () => PermissionManager.canEditProductSelection(),
    canDeleteProductSelection: () => PermissionManager.canDeleteProductSelection(),
    canApproveProductSelection: () => PermissionManager.canApproveProductSelection(),
    canViewPOCreation: () => PermissionManager.canViewPOCreation(),
    canEditPOCreation: () => PermissionManager.canEditPOCreation(),
    canDeletePOCreation: () => PermissionManager.canDeletePOCreation(),
    canViewDeliveryInfo: () => PermissionManager.canViewDeliveryInfo(),
    canEditDeliveryInfo: () => PermissionManager.canEditDeliveryInfo(),
    canDeleteDeliveryInfo: () => PermissionManager.canDeleteDeliveryInfo(),
    canViewReviewInfo: () => PermissionManager.canViewReviewInfo(),
    canApproveReviewInfo: () => PermissionManager.canApproveReviewInfo(),
    canViewDeliveryDocuments: () => PermissionManager.canViewDeliveryDocuments(),
    canEditDeliveryDocuments: () => PermissionManager.canEditDeliveryDocuments(),
    canDeleteDeliveryDocuments: () => PermissionManager.canDeleteDeliveryDocuments(),
    canViewInventoryUsage: () => PermissionManager.canViewInventoryUsage(),
    canEditInventoryUsage: () => PermissionManager.canEditInventoryUsage(),
    canViewInvoicePayment: () => PermissionManager.canViewInvoicePayment(),
    canEditInvoicePayment: () => PermissionManager.canEditInvoicePayment(),
    canViewDeliveryDetails: () => PermissionManager.canViewDeliveryDetails(),
    canEditDeliveryDetails: () => PermissionManager.canEditDeliveryDetails(),
    canViewFinalApproval: () => PermissionManager.canViewFinalApproval(),
    canApproveFinalDelivery: () => PermissionManager.canApproveFinalDelivery(),
    isAdminOrAbove: permissions ? PermissionManager.isAdminOrAbove() : false,
    isManagerOrAbove: permissions ? PermissionManager.isManagerOrAbove() : false,
    isCompanyOwner: permissions ? PermissionManager.isCompanyOwner() : false,
    canManageUsers: permissions ? PermissionManager.canManageUsers() : false,
    userRole: permissions ? PermissionManager.getUserRole() : 'unknown',
    companyId: permissions ? PermissionManager.getCompanyId() : null,
    hierarchyLevel: permissions ? PermissionManager.getHierarchyLevel() : 999,
    companyIsActive: permissions ? permissions.company_is_active : true,
    isOwner: permissions ? permissions.is_company_owner : false,
    refresh
  };
}

// Higher-order component for protecting routes
export function withPermission(WrappedComponent, requiredPermission, accessType = 'read') {
  return function PermissionProtectedComponent(props) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(requiredPermission, accessType)) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 3
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 500, color: '#555' }}>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ color: '#777', mb: 3 }}>
            You don't have permission to access this page.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{
              backgroundColor: '#1e1e1e',
              color: '#fff',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#333' }
            }}
          >
            Go Back
          </Button>
        </Box>
      );
    }

    return <WrappedComponent {...props} />;
  };
}